import type { IsoDate } from './common';
import { CLOSED_DAYS_MAX, closedDayGroups, closedDayGroupText, dayText, type ClosedDay, type ShipByImpact } from './ship-by';

// Türkiye's official holidays (resmî tatil: 2429 sayılı Ulusal Bayram ve Genel Tatiller Hakkında Kanun),
// to enter as a warehouse's closed days at once: "Resmî tatilleri ekle" in the definition dialog and
// `ship-by:holidays <DEPO_KODU>` on the server. The days the law fixes come every year; Ramazan Bayramı
// (3 days) and Kurban Bayramı (4 days) follow the Hijri calendar, on the dates of the calendar of
// Diyanet İşleri Başkanlığı. The arife of a bayram (and 28 Ekim) is off from 13:00 only: the warehouse
// ships in the morning, so an arife is no closed day; it stays in the list as information.
//
// The list ends on TURKISH_HOLIDAYS_UNTIL. For a later year, add its religious holidays once Diyanet and
// the Resmî Gazete publish them, with the fixed days of that year (one entry, a combined note, where a
// bayram falls on one of them), and move TURKISH_HOLIDAYS_UNTIL to that year's end.

/** A day of the list: a closed day, or an arife (off from 13:00, never a closed day) */
export interface TurkishHoliday {
  readonly date: IsoDate;
  readonly note: string;
  readonly halfDay: boolean;
}

const RAMAZAN = 'Ramazan Bayramı';
const KURBAN = 'Kurban Bayramı';
const NEW_YEAR = 'Yılbaşı';
const APRIL_23 = 'Ulusal Egemenlik ve Çocuk Bayramı';
const MAY_1 = 'Emek ve Dayanışma Günü';
const MAY_19 = "Atatürk'ü Anma, Gençlik ve Spor Bayramı";
const JULY_15 = 'Demokrasi ve Millî Birlik Günü';
const AUGUST_30 = 'Zafer Bayramı';
const REPUBLIC = 'Cumhuriyet Bayramı';

const off = (date: IsoDate, note: string): TurkishHoliday => ({ date, note, halfDay: false });
const arife = (date: IsoDate, bayram: string): TurkishHoliday => ({ date, note: `${bayram} arifesi`, halfDay: true });

/** The official holidays to TURKISH_HOLIDAYS_UNTIL, oldest first */
export const TURKISH_HOLIDAYS: readonly TurkishHoliday[] = [
  // 2026
  off('2026-01-01', NEW_YEAR),
  arife('2026-03-19', RAMAZAN),
  off('2026-03-20', RAMAZAN),
  off('2026-03-21', RAMAZAN),
  off('2026-03-22', RAMAZAN),
  off('2026-04-23', APRIL_23),
  off('2026-05-01', MAY_1),
  off('2026-05-19', MAY_19),
  arife('2026-05-26', KURBAN),
  off('2026-05-27', KURBAN),
  off('2026-05-28', KURBAN),
  off('2026-05-29', KURBAN),
  off('2026-05-30', KURBAN),
  off('2026-07-15', JULY_15),
  off('2026-08-30', AUGUST_30),
  arife('2026-10-28', REPUBLIC),
  off('2026-10-29', REPUBLIC),
  // 2027
  off('2027-01-01', NEW_YEAR),
  arife('2027-03-08', RAMAZAN),
  off('2027-03-09', RAMAZAN),
  off('2027-03-10', RAMAZAN),
  off('2027-03-11', RAMAZAN),
  off('2027-04-23', APRIL_23),
  off('2027-05-01', MAY_1),
  arife('2027-05-15', KURBAN),
  off('2027-05-16', KURBAN),
  off('2027-05-17', KURBAN),
  off('2027-05-18', KURBAN),
  // the fourth day of Kurban Bayramı is 19 Mayıs as well: one closed day
  off('2027-05-19', `${KURBAN}; ${MAY_19}`),
  off('2027-07-15', JULY_15),
  off('2027-08-30', AUGUST_30),
  arife('2027-10-28', REPUBLIC),
  off('2027-10-29', REPUBLIC),
];

/** The last day the list covers: holidays after it are not in it yet (see above) */
export const TURKISH_HOLIDAYS_UNTIL: IsoDate = '2027-12-31';

/** "Liste 2027 yılı sonuna kadar…": how far the list goes (`today`: whether it has ended), for the panel and the server command */
export function turkishHolidaysCoverText(today?: IsoDate): string {
  const year = TURKISH_HOLIDAYS_UNTIL.slice(0, 4);
  const later = 'sonraki yılların bayram tarihleri Diyanet ve Resmî Gazete yayımlayınca eklenecek';
  return today !== undefined && today > TURKISH_HOLIDAYS_UNTIL ? `Resmî tatil listesi ${year} yılı sonunda bitti; ${later}.` : `Liste ${year} yılı sonuna kadar; ${later}.`;
}

/** What adding the official holidays to a warehouse's closed days does. */
export interface TurkishHolidayAddition {
  /** the closed days with the holidays added, oldest first */
  readonly closedDays: readonly ClosedDay[];
  /** the holidays added: from today on, not entered yet */
  readonly added: readonly ClosedDay[];
  /** days of the list from today on entered already: they stay as they are, with the note written for them */
  readonly existing: readonly ClosedDay[];
  /** holidays left out: more than CLOSED_DAYS_MAX closed days */
  readonly overLimit: readonly ClosedDay[];
  /** arifes from today on: not added, the warehouse ships in the morning */
  readonly halfDays: readonly TurkishHoliday[];
}

/**
 * The closed days with the official holidays from `today` (the warehouse's day) on added: past ones
 * and arifes are left out, a day entered already keeps its note, and no more than CLOSED_DAYS_MAX in
 * all (the earliest holidays first). Nothing is removed.
 */
export function withTurkishHolidays(closedDays: readonly ClosedDay[], today: IsoDate): TurkishHolidayAddition {
  const entered = new Map(closedDays.map((d) => [d.date, d]));
  const added: ClosedDay[] = [];
  const existing: ClosedDay[] = [];
  const overLimit: ClosedDay[] = [];
  const halfDays: TurkishHoliday[] = [];
  let room = CLOSED_DAYS_MAX - entered.size;
  for (const h of TURKISH_HOLIDAYS) {
    if (h.date < today) continue;
    const there = entered.get(h.date);
    // an arife entered as closed by hand is the warehouse's choice: it stays, like any day entered
    if (there) existing.push(there);
    else if (h.halfDay) halfDays.push(h);
    else if (room > 0) {
      added.push({ date: h.date, note: h.note });
      room--;
    } else overLimit.push({ date: h.date, note: h.note });
  }
  return { closedDays: [...entered.values(), ...added].sort((a, b) => a.date.localeCompare(b.date)), added, existing, overLimit, halfDays };
}

/** "28.10.2026 (Cumhuriyet Bayramı arifesi), 08.03.2027 (Ramazan Bayramı arifesi)" */
const daysText = (days: readonly { date: IsoDate; note: string | null }[]) => closedDayGroups(days).map(closedDayGroupText).join(', ');

/** The arifes, as the panel and the server command name them */
export const halfDaysText = (days: readonly TurkishHoliday[]) => `Arife yarım gün: kapalı sayılmadı (öğleden önce kargo çıkar): ${daysText(days)}.`;

/**
 * Why nothing is added though there is room, for the panel and the server command: the holidays from
 * today on are closed days already, or the list has none left after today (past its last holiday; the
 * cover text then says how far it goes), and then nothing of it is entered, whatever the warehouse has.
 */
export const noHolidayToAddText = (a: TurkishHolidayAddition) =>
  a.existing.length > 0
    ? 'Eklenecek resmî tatil yok: listedeki bugünden sonraki tatillerin hepsi zaten kapalı gün.'
    : 'Eklenecek resmî tatil yok: listede bugünden sonra resmî tatil kalmadı.';

/** `ship-by:holidays --list`: the built-in list, year by year, a bayram on one line */
export function turkishHolidayListLines(): string[] {
  const lines = [`Türkiye'nin resmî tatilleri (2429 sayılı Kanun), ${TURKISH_HOLIDAYS_UNTIL.slice(0, 4)} yılı sonuna kadar:`];
  for (const year of [...new Set(TURKISH_HOLIDAYS.map((h) => h.date.slice(0, 4)))]) {
    lines.push('', year);
    const days = TURKISH_HOLIDAYS.filter((h) => h.date.startsWith(year)).map((h) => ({
      date: h.date,
      note: h.halfDay ? `${h.note}: yarım gün, 13:00'ten sonra; kapalı gün sayılmaz` : h.note,
    }));
    for (const g of closedDayGroups(days)) lines.push(`  ${closedDayGroupText(g)}`);
  }
  lines.push('', turkishHolidaysCoverText());
  return lines;
}

/** One run of `ship-by:holidays <DEPO_KODU> [--dry-run]`. */
export interface TurkishHolidayRun {
  readonly warehouseCode: string;
  /** the warehouse's day the holidays were taken from */
  readonly today: IsoDate;
  readonly dryRun: boolean;
  /** the definition was saved: something was added, and not as a dry run */
  readonly saved: boolean;
  readonly addition: TurkishHolidayAddition;
  /** what the holidays change (or would change) in the warehouse's orders; closed ones are never recomputed */
  readonly impact: ShipByImpact;
}

const count = (n: number) => n.toLocaleString('tr-TR');

/** The run as the command prints it for the people operating the system. */
export function turkishHolidayRunLines(run: TurkishHolidayRun): string[] {
  const { addition: a, impact } = run;
  const lines = [`${run.warehouseCode} deposu, bugün ${dayText(run.today)}${run.dryRun ? ' (deneme: hiçbir şey kaydedilmedi)' : ''}`, ''];
  if (a.added.length === 0) {
    lines.push(a.overLimit.length > 0 ? `Eklenecek yer yok: depoda en fazla ${CLOSED_DAYS_MAX} kapalı gün olabilir.` : noHolidayToAddText(a));
  } else {
    lines.push(`${run.dryRun ? 'Eklenecek' : 'Eklendi'} (${a.added.length} gün):`);
    for (const g of closedDayGroups(a.added)) lines.push(`  ${closedDayGroupText(g)}`);
  }
  if (a.existing.length > 0) {
    lines.push('', `Zaten girilmiş, olduğu gibi bırakıldı (${a.existing.length} gün):`);
    for (const g of closedDayGroups(a.existing)) lines.push(`  ${closedDayGroupText(g)}`);
  }
  if (a.overLimit.length > 0 && a.added.length > 0) {
    lines.push('', `En fazla ${CLOSED_DAYS_MAX} kapalı gün olabildiği için eklenmedi (${a.overLimit.length} gün): ${daysText(a.overLimit)}`);
  } else if (a.overLimit.length > 0) {
    lines.push(`Eklenemeyen (${a.overLimit.length} gün): ${daysText(a.overLimit)}`);
  }
  if (a.halfDays.length > 0) lines.push('', halfDaysText(a.halfDays));
  lines.push('');
  if (a.added.length === 0) lines.push('Tanım değişmedi; siparişlere dokunulmadı.');
  else {
    const open =
      impact.open === 0
        ? 'Açık sipariş yok.'
        : impact.openChanged === 0
          ? `Açık siparişlerin (${count(impact.open)}) son kargo zamanı ${run.dryRun ? 'değişmez' : 'değişmedi'}.`
          : `${count(impact.openChanged)} açık siparişin son kargo zamanı ${run.dryRun ? 'değişir' : 'değişti'} (toplam ${count(impact.open)} açık sipariş).`;
    lines.push(`${open} Kapanmış siparişler eski tanımla kalır.`);
    if (run.saved) lines.push('Tanım kaydedildi; Performans kartında “Son değişiklik · sunucu komutuyla” görünür.');
    else lines.push('Kaydetmek için komutu --dry-run olmadan çalıştırın.');
  }
  lines.push(turkishHolidaysCoverText(run.today));
  return lines;
}
